import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Label,
} from '@patternfly/react-core';
import type { DomainName } from '../../../types/models/DomainName';
import { formatDateNumeric } from '../../../utils/dateFormat';
import DomainAutoRenewField from './DomainAutoRenewField';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Render an escalating "days left" label for an expiry-style date:
 * - 30+ days away → success (green)
 * - 15–29 days away → warning
 * - 1–14 days away → danger
 * - already past → danger ("Expired N days ago")
 * Returns null when the date is null/unparseable.
 */
function daysLeftLabel(value: string | null): React.ReactNode {
  if (value === null) return null;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return null;

  const diffMs = ts - Date.now();

  if (diffMs <= 0) {
    const daysAgo = Math.floor(-diffMs / MS_PER_DAY);
    const text = daysAgo === 0 ? 'Expired today' : `Expired ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
    return (
      <Label status="danger" isCompact>
        {text}
      </Label>
    );
  }

  const daysLeft = Math.ceil(diffMs / MS_PER_DAY);
  const status: 'danger' | 'warning' | 'success' = daysLeft < 15 ? 'danger' : daysLeft < 30 ? 'warning' : 'success';
  return (
    <Label status={status} isCompact>
      {`${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
    </Label>
  );
}

/** Format a date as dd/mm/yyyy with an inline "days left" label when relevant. */
function renderDateWithDaysLeft(value: string | null): React.ReactNode {
  const label = daysLeftLabel(value);
  return (
    <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
      <FlexItem>{formatDateNumeric(value)}</FlexItem>
      {label && <FlexItem>{label}</FlexItem>}
    </Flex>
  );
}

interface DomainDatesTabProps {
  domainName: DomainName;
}

function DomainDatesTab({ domainName }: DomainDatesTabProps) {
  return (
    <Card>
      <CardTitle>Dates</CardTitle>
      <CardBody>
        <DescriptionList columnModifier={{ default: '1Col', sm: '2Col' }}>
          <DescriptionListGroup>
            <DescriptionListTerm>Registered</DescriptionListTerm>
            <DescriptionListDescription>{formatDateNumeric(domainName.created_at)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Last updated</DescriptionListTerm>
            <DescriptionListDescription>{formatDateNumeric(domainName.updated_at)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Expires</DescriptionListTerm>
            <DescriptionListDescription>{renderDateWithDaysLeft(domainName.expires_at)}</DescriptionListDescription>
          </DescriptionListGroup>
          {/* Absent until the backend auto-renew feature is deployed — hide the row rather than guess. */}
          {domainName.auto_renew && (
            <DescriptionListGroup>
              <DescriptionListTerm>Auto-renew</DescriptionListTerm>
              <DescriptionListDescription>
                <DomainAutoRenewField domainName={domainName} autoRenew={domainName.auto_renew} />
              </DescriptionListDescription>
            </DescriptionListGroup>
          )}
          <DescriptionListGroup>
            <DescriptionListTerm>Vanishes</DescriptionListTerm>
            <DescriptionListDescription>{renderDateWithDaysLeft(domainName.vanishes_at)}</DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </CardBody>
    </Card>
  );
}

export default DomainDatesTab;
